"use client";

import "@assistant-ui/react-markdown/styles/dot.css";

import {
  type CodeHeaderProps,
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import { type FC, memo, useState } from "react";
import { CheckIcon, CopyIcon } from "lucide-react";

import { TooltipIconButton } from "@/components/tooltip-icon-button";
import { useT } from "@/locale-context";
import { TypologyCard } from "@/components/typology-card";
import { SlotPicker } from "@/components/slot-picker";
import { BookingConfirmationCard } from "@/components/booking-confirmation-card";
import { CsatRating } from "@/components/csat-rating";
import { cn } from "@/lib/utils";
import type {
  BookingConfirmationPayload,
  CsatRatingPayload,
  SlotPickerPayload,
  TypologyResult,
} from "@/lib/api";

/** Languages that represent structured content blocks, not actual code. */
const CONTENT_BLOCK_LANGUAGES = new Set([
  "typology-card",
  "slot-picker",
  "booking-confirmation",
  "csat-rating",
]);

// @assistant-ui/react-markdown's CodeOverride extracts the fence language with
// /language-(\w+)/ — \w does NOT match hyphens, so "language-csat-rating"
// becomes "csat" by the time it reaches CodeHeader. Mirror every content-block
// language as its pre-hyphen prefix so the header is still suppressed.
const CONTENT_BLOCK_HEADER_TOKENS = new Set(
  [...CONTENT_BLOCK_LANGUAGES].flatMap((l) => [l, l.split("-")[0]]),
);

const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md"
      components={defaultComponents}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

const CodeHeader: FC<CodeHeaderProps> = ({ language, code }) => {
  // Content-block code blocks are rendered as UI cards — no header needed.
  if (language && CONTENT_BLOCK_HEADER_TOKENS.has(language)) return null;

  const t = useT();
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const onCopy = () => {
    if (!code || isCopied) return;
    copyToClipboard(code);
  };

  return (
    <div className="aui-code-header-root mt-2.5 flex items-center justify-between rounded-t-lg border border-border/50 border-b-0 bg-muted/50 px-3 py-1.5 text-xs">
      <span className="aui-code-header-language font-medium text-muted-foreground lowercase">
        {language}
      </span>
      <TooltipIconButton tooltip={t("markdown.copy")} onClick={onCopy}>
        {!isCopied && <CopyIcon />}
        {isCopied && <CheckIcon />}
      </TooltipIconButton>
    </div>
  );
};

const useCopyToClipboard = ({
  copiedDuration = 3000,
}: {
  copiedDuration?: number;
} = {}) => {
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const copyToClipboard = (value: string) => {
    if (!value) return;

    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), copiedDuration);
    });
  };

  return { isCopied, copyToClipboard };
};

const defaultComponents = memoizeMarkdownComponents({
  h1: ({ className, ...props }) => (
    <h1
      className={cn(
        "aui-md-h1 mb-2 scroll-m-20 font-semibold text-base first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={cn(
        "aui-md-h2 mt-3 mb-1.5 scroll-m-20 font-semibold text-sm first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={cn(
        "aui-md-h3 mt-2.5 mb-1 scroll-m-20 font-semibold text-sm first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h4: ({ className, ...props }) => (
    <h4
      className={cn(
        "aui-md-h4 mt-2 mb-1 scroll-m-20 font-medium text-sm first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h5: ({ className, ...props }) => (
    <h5
      className={cn(
        "aui-md-h5 mt-2 mb-1 font-medium text-sm first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  h6: ({ className, ...props }) => (
    <h6
      className={cn(
        "aui-md-h6 mt-2 mb-1 font-medium text-sm first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={cn(
        "aui-md-p my-2.5 leading-normal first:mt-0 last:mb-0",
        className,
      )}
      {...props}
    />
  ),
  a: ({ className, ...props }) => (
    <a
      className={cn(
        "aui-md-a text-primary underline underline-offset-2 hover:text-primary/80",
        className,
      )}
      {...props}
    />
  ),
  blockquote: ({ className, ...props }) => (
    <blockquote
      className={cn(
        "aui-md-blockquote my-2.5 border-muted-foreground/30 border-l-2 pl-3 text-muted-foreground italic",
        className,
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={cn(
        "aui-md-ul my-2 ml-4 list-disc marker:text-muted-foreground [&>li]:mt-1",
        className,
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={cn(
        "aui-md-ol my-2 ml-4 list-decimal marker:text-muted-foreground [&>li]:mt-1",
        className,
      )}
      {...props}
    />
  ),
  hr: ({ className, ...props }) => (
    <hr
      className={cn("aui-md-hr my-2 border-muted-foreground/20", className)}
      {...props}
    />
  ),
  table: ({ className, ...props }) => (
    <table
      className={cn(
        "aui-md-table my-2 w-full border-separate border-spacing-0 overflow-y-auto",
        className,
      )}
      {...props}
    />
  ),
  th: ({ className, ...props }) => (
    <th
      className={cn(
        "aui-md-th bg-muted px-2 py-1 text-left font-medium first:rounded-tl-lg last:rounded-tr-lg [[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  td: ({ className, ...props }) => (
    <td
      className={cn(
        "aui-md-td border-muted-foreground/20 border-b border-l px-2 py-1 text-left last:border-r [[align=center]]:text-center [[align=right]]:text-right",
        className,
      )}
      {...props}
    />
  ),
  tr: ({ className, ...props }) => (
    <tr
      className={cn(
        "aui-md-tr m-0 border-b p-0 first:border-t [&:last-child>td:first-child]:rounded-bl-lg [&:last-child>td:last-child]:rounded-br-lg",
        className,
      )}
      {...props}
    />
  ),
  li: ({ className, ...props }) => (
    <li className={cn("aui-md-li leading-normal", className)} {...props} />
  ),
  sup: ({ className, ...props }) => (
    <sup
      className={cn("aui-md-sup [&>a]:text-xs [&>a]:no-underline", className)}
      {...props}
    />
  ),
  pre: function Pre({ children, className, ...props }) {
    // Detect content-block code blocks and render as rich UI cards.
    // react-markdown wraps fenced code as: <pre><code class="language-X">...</code></pre>
    const child = Array.isArray(children) ? children[0] : children;
    const childProps = (child as React.ReactElement<{ className?: string; children?: React.ReactNode }>)?.props;
    const codeClassName: string = childProps?.className ?? "";
    const language = codeClassName.replace("language-", "");

    console.log("[Pre]", {
      childrenIsArray: Array.isArray(children),
      childrenLength: Array.isArray(children) ? children.length : undefined,
      childType: typeof child,
      childIsValidElement: !!(child && typeof child === "object" && "props" in (child as object)),
      codeClassName,
      language,
      matchesContentBlock: CONTENT_BLOCK_LANGUAGES.has(language),
      childPropsChildrenType: typeof childProps?.children,
      childPropsChildrenIsArray: Array.isArray(childProps?.children),
      rawPreview: String(childProps?.children ?? "").slice(0, 120),
    });

    // Two detection paths:
    //   (A) fence info string survived → dispatch on `language`
    //   (B) info string got stripped by the markdown pipeline → parse the
    //       body and dispatch on the `__block` discriminator injected by
    //       ApiRuntimeProvider.tsx
    const rawChildren = childProps?.children;
    const rawText = typeof rawChildren === "string" ? rawChildren.trim() : "";

    if (CONTENT_BLOCK_LANGUAGES.has(language) && rawText.length > 0) {
      try {
        const data: unknown = JSON.parse(rawText);
        if (language === "typology-card") {
          return <TypologyCard data={data as TypologyResult} />;
        }
        if (language === "slot-picker") {
          return <SlotPicker data={data as SlotPickerPayload} />;
        }
        if (language === "booking-confirmation") {
          return (
            <BookingConfirmationCard
              data={data as BookingConfirmationPayload}
            />
          );
        }
        if (language === "csat-rating") {
          return <CsatRating data={data as CsatRatingPayload} />;
        }
      } catch {
        // Parse failed — fall through to default code block rendering
      }
    }

    if (!language && rawText.startsWith("{") && rawText.includes('"__block"')) {
      try {
        const parsed = JSON.parse(rawText) as { __block?: string; data?: unknown };
        const blockType = parsed.__block;
        if (blockType && CONTENT_BLOCK_LANGUAGES.has(blockType)) {
          const blockData = parsed.data;
          if (blockType === "typology-card") {
            return <TypologyCard data={blockData as TypologyResult} />;
          }
          if (blockType === "slot-picker") {
            return <SlotPicker data={blockData as SlotPickerPayload} />;
          }
          if (blockType === "booking-confirmation") {
            return (
              <BookingConfirmationCard
                data={blockData as BookingConfirmationPayload}
              />
            );
          }
          if (blockType === "csat-rating") {
            return <CsatRating data={blockData as CsatRatingPayload} />;
          }
        }
      } catch {
        // Fall through
      }
    }

    return (
      <pre
        className={cn(
          "aui-md-pre overflow-x-auto rounded-t-none rounded-b-lg border border-border/50 border-t-0 bg-muted/30 p-3 text-xs leading-relaxed",
          className,
        )}
        {...props}
      >
        {children}
      </pre>
    );
  },
  code: function Code({ className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(
          !isCodeBlock &&
            "aui-md-inline-code rounded-md border border-border/50 bg-muted/50 px-1.5 py-0.5 font-mono text-[0.85em]",
          className,
        )}
        {...props}
      />
    );
  },
  CodeHeader,
});
